import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {
	ActivityIndicator,
	SafeAreaView,
	StyleSheet,
	View,
	Text,
	Image,
	ScrollView,
} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';

import {UserContext} from '../contexts/UserContext';
import {ThemeContext, WEB_MAX_WIDTH} from '../contexts/ThemeContext';
import Api from '../constants/Api';
import Chat from './MainScreen/Chat/Chat';
import capitalize from '../utils/capitalize';
import {useTranslation} from 'react-i18next';

const QueryScreen = () => {
	const {userToken} = useContext(UserContext);
	const theme = useContext(ThemeContext);
	const styles = useMemo(() => getStyles(theme), [theme]);
	const {t} = useTranslation();
	const {params} = useRoute();
	const {setOptions} = useNavigation();

	const [loading, setLoading] = useState(true);
	const [query, setQuery] = useState(null);

	const fetchQuery = useCallback(async () => {
		if (params.id) {
			setLoading(true);

			const response = await fetch(`${Api.apiBaseUrl}/queries/${params.id}.json?user_token=${userToken}`, {method: 'GET'});
			const data = await response.json();

			if (data.error) {
				// TODO: handle error
				console.log(data.error);
				return;
			}

			setOptions({title: data.discussion.name});

			data.steps.unshift({
				id: -1,
				name: 'Secrétaire AI',
				icon: 'icon-white.png',
			});

			if (data.device && data.device.device_type) {
				let icon = 'icon-white.png';

				switch(data.device.device_type) {
					case 'desktop':
						icon = 'desktop.png';
						break;
					case 'phone':
					case 'mobile':
						icon = 'mobile.png';
						break;
					case 'tablet':
						icon = 'tablet.png';
						break;
				}

				data.steps.unshift({
					id: -2,
					name: capitalize(data.device.device_type),
					icon,
				});
			}

			setQuery(data);
			setLoading(false);
		}
	}, [params.id, userToken, setOptions]);

	useEffect(() => {
		fetchQuery();
	}, [userToken, fetchQuery]);

	return (
		<SafeAreaView style={styles.safeArea}>
			<View style={styles.container}>
				{loading
					? (
						<View style={styles.loaderContainer}>
							<ActivityIndicator size="large"/>
						</View>
					)
					: (
						<ScrollView style={styles.scrollView}>
							<Chat
								queries={[query]}
								disableResponseLinks
								disableQueryLinks
							/>

							<View style={styles.titleContainer}>
								<Text style={styles.title}>
									{t('QueryScreen.stepTitle', 'Steps')}
								</Text>
							</View>

							<View style={styles.stepsContainer}>
								{(query.steps || []).map((step, i) => (
									<View key={step.id} style={{alignItems: 'center'}}>
										{i !== 0 && (
											<Text style={styles.arrow}>
												↓
											</Text>
										)}

										<View style={styles.step}>
											<Image
												source={{uri: `${Api.apiBaseUrl}/images/step-icons/${step.icon}`}}
												style={styles.stepIcon}
											/>

											<Text style={styles.stepName}>
												{step.name}
											</Text>
										</View>
									</View>
								))}
							</View>
						</ScrollView>
					)}
			</View>
		</SafeAreaView>
	);
};

const getStyles = theme => StyleSheet.create({
	safeArea: {
		flex:1,
		width: '100%',
		backgroundColor: theme.headerBackground,
	},
	container: {
		flex: 1,
		width: '100%',
		backgroundColor: theme.backgroundPrimary,
	},
	loaderContainer: {
		flex: 1,
		justifyContent: 'center',
	},
	scrollView: {paddingTop: 6},
	titleContainer: {alignItems: 'center', paddingTop: 20},
	title: {fontSize: 20, color: theme.textSecondary},
	stepsContainer: {
		flexDirection: 'column',
		alignItems: 'center',
		gap: 12,
		paddingVertical: 20,
	},
	arrow: {fontSize: 36, color: theme.colorSecondary, marginBottom: 12},
	step: {
		width: 200,
		height: 200,
		borderRadius: 100,
		backgroundColor: '#212529',
		justifyContent: 'center',
		alignItems: 'center',
		borderWidth: 1,
		borderColor: theme.inputBorderColor,
	},
	stepIcon: {
		width: 70,
		height: 70,
		marginBottom: 8,
	},
	stepName: {color: '#fff', fontWeight: 'bold', maxWidth: 120},
});

export default QueryScreen;
